"use client";

import type React from "react";
import { useEffect, useState } from "react";

type TransactionLine = {
  id: number;
  x?: number;
  y?: number;
  isHorizontal: boolean;
  delay: number;
  duration: number;
  length: number;
  opacity: number;
};

export function TransactionBackground() {
  const [lines, setLines] = useState<TransactionLine[]>([]);

  useEffect(() => {
    // Generate random transaction flow lines
    const generateLines = () => {
      const newLines: TransactionLine[] = [];
      // Horizontal lines
      for (let i = 0; i < 12; i++) {
        newLines.push({
          id: i,
          y: Math.random() * 100,
          x: Math.random() * 100, // added random x position for horizontal lines
          isHorizontal: true,
          delay: Math.random() * 5,
          duration: 3 + Math.random() * 2,
          length: 15 + Math.random() * 25,
          opacity: 0.2 + Math.random() * 0.4,
        });
      }
      // Vertical lines
      for (let i = 0; i < 12; i++) {
        newLines.push({
          id: 100 + i,
          x: Math.random() * 100,
          isHorizontal: false,
          delay: Math.random() * 5,
          duration: 3.5 + Math.random() * 2,
          length: 15 + Math.random() * 25,
          opacity: 0.2 + Math.random() * 0.35,
        });
      }
      setLines(newLines);
    };

    generateLines();
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      {/* Animated transaction flows */}
      {lines.map((line) =>
        line.isHorizontal ? (
          <div
            className="absolute h-px w-full"
            key={line.id}
            style={{ top: `${line.y}%`, left: `${line.x}%` }}
          >
            {" "}
            {/* added left positioning */}
            <div
              className="transaction-flow-h-random flex h-full w-full items-center bg-gradient-to-r from-transparent via-primary/40 to-transparent"
              style={
                {
                  animationDelay: `${line.delay}s`,
                  animationDuration: `${line.duration}s`,
                  "--flow-length": `${line.length}%`,
                  "--flow-opacity": line.opacity,
                } as React.CSSProperties & {
                  "--flow-length": string;
                  "--flow-opacity": number;
                }
              }
            >
              <div className="absolute left-0 h-2 w-2 rounded-full bg-primary/60" />
            </div>
          </div>
        ) : (
          <div
            className="absolute h-full w-px"
            key={line.id}
            style={{ left: `${line.x}%` }}
          >
            <div
              className="transaction-flow-v-random flex h-full w-full items-start justify-center bg-gradient-to-b from-transparent via-accent/40 to-transparent"
              style={
                {
                  animationDelay: `${line.delay}s`,
                  animationDuration: `${line.duration}s`,
                  "--flow-length": `${line.length}%`,
                  "--flow-opacity": line.opacity,
                } as React.CSSProperties & {
                  "--flow-length": string;
                  "--flow-opacity": number;
                }
              }
            >
              <div className="absolute top-0 h-2 w-2 rounded-full bg-accent/60" />
            </div>
          </div>
        )
      )}
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(90deg, transparent 24%, hsla(0,0%,100%,.05) 25%, hsla(0,0%,100%,.05) 26%, transparent 27%, transparent 74%, hsla(0,0%,100%,.05) 75%, hsla(0,0%,100%,.05) 76%, transparent 77%, transparent),
            linear-gradient(0deg, transparent 24%, hsla(0,0%,100%,.05) 25%, hsla(0,0%,100%,.05) 26%, transparent 27%, transparent 74%, hsla(0,0%,100%,.05) 75%, hsla(0,0%,100%,.05) 76%, transparent 77%, transparent)
          `,
          backgroundSize: "60px 60px",
        }}
      />
    </div>
  );
}
